import { useCanAccess, useTranslate } from 'ra-core';
import { Link, useLocation, matchPath } from 'react-router';
import { getPublisherIdFromPath } from '@/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { ChartLineUpIcon, RocketLaunchIcon } from '@phosphor-icons/react';
import { UserMenu } from '@/components/admin/user-menu';
import { ThemeToggle } from '@/components/admin/theme-toggle';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import GolootLogo from '@/components/ui/GolootLogo';
export function AppSidebar() {
  const { open, setOpen, openMobile, setOpenMobile } = useSidebar();
  const { pathname } = useLocation();
  const publisherId = getPublisherIdFromPath(pathname);

  const handleMenuItemClick = () => {
    if (openMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader className="p-0 mb-3 group-data-[collapsible=icon]:mb-0 group-data-[collapsible=icon]:py-4">
        {open ? (
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/" className="shrink-0">
              <GolootLogo className="border rounded-sm" />
            </Link>
            <div className="min-w-0">
              <div className="text-sm font-semibold leading-tight tracking-tight">
                Goloot
              </div>
              <div className="text-xs leading-4 tracking-tight">
                for Publishers
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(true)}
              className="h-10 w-10 p-0"
            >
              <GolootLogo className="border rounded-sm" />
            </Button>
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="px-0">
          <SidebarGroupContent>
            <SidebarMenu>
              <OffersMenuItem
                publisherId={publisherId}
                onClick={handleMenuItemClick}
              />
              <PerformanceMenuItem
                publisherId={publisherId}
                onClick={handleMenuItemClick}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="mt-auto px-0 group-data-[collapsible=icon]:p-0">
          <SidebarGroupContent>
            <ThemeToggle />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="px-0 pb-0 group-data-[collapsible=icon]:py-4">
        <UserMenu />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function PerformanceMenuItem({
  publisherId,
  onClick,
}: {
  publisherId?: string;
  onClick?: () => void;
}) {
  const t = useTranslate();
  const { pathname } = useLocation();
  const { canAccess: canAccessReports } = useCanAccess({
    resource: 'reports',
    action: 'list',
  });
  const to = `/publisher/${publisherId}/performance`;
  const isActive = !!matchPath({ path: to, end: false }, pathname);

  if (canAccessReports) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={isActive}>
          <Link to={to} onClick={onClick}>
            <ChartLineUpIcon />
            <span className="group-data-[collapsible=icon]:hidden">
              {t('menu.performance')}
            </span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  // No reports access yet — show a "Coming soon" tooltip on hover (works in both
  // expanded and collapsed states, unlike the built-in collapsed-only tooltip).
  return (
    <SidebarMenuItem>
      <Tooltip>
        <TooltipTrigger asChild>
          <SidebarMenuButton
            isActive={isActive}
            aria-disabled
            className="text-muted-foreground cursor-not-allowed"
          >
            <ChartLineUpIcon />
            <span className="group-data-[collapsible=icon]:hidden">
              {t('menu.performance')}
            </span>
          </SidebarMenuButton>
        </TooltipTrigger>
        <TooltipContent>Coming soon</TooltipContent>
      </Tooltip>
    </SidebarMenuItem>
  );
}

function OffersMenuItem({
  publisherId,
  onClick,
}: {
  publisherId?: string;
  onClick?: () => void;
}) {
  const t = useTranslate();
  const { pathname } = useLocation();
  const to = `/publisher/${publisherId}/offers/pending`;
  const isActive = !!(
    matchPath(
      { path: `/publisher/${publisherId}/offers`, end: false },
      pathname
    ) ||
    matchPath({ path: `/publisher/${publisherId}/offer`, end: false }, pathname)
  );

  if (!publisherId) return null;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <Link to={to} onClick={onClick}>
          <RocketLaunchIcon />
          <span className="group-data-[collapsible=icon]:hidden">
            {t('menu.offers')}
          </span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
